import { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { useEffect, useRef } from "react";

export function useObserveVisibleResults(
  results: TypeaheadResult[],
  isTypeaheadOpen: boolean,
  onListItemView: (item: TypeaheadResult, index: number) => void,
) {
  const intersectionObserver = useRef<IntersectionObserver>();
  const visibleResultsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (onListItemView === undefined) return;
    if (!isTypeaheadOpen) {
      visibleResultsRef.current = new Set();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const nVisibleResults: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const { target } = entry;
            const entryUuid = target.getAttribute("data-x-uuid");
            if (entryUuid) {
              nVisibleResults.push(entryUuid);
              if (!visibleResultsRef.current.has(entryUuid)) {
                const index = results.findIndex((r) => r.uuid === entryUuid);
                if (index >= 0) {
                  const item = results[index];
                  onListItemView(item, index);
                }
              }
            }
          }
        });
        visibleResultsRef.current = new Set([
          ...visibleResultsRef.current,
          ...nVisibleResults,
        ]);
      },
      { threshold: 0.75 },
    );
    intersectionObserver.current = observer;

    return () => {
      intersectionObserver.current = undefined;
    };
  }, [results, isTypeaheadOpen, onListItemView]);

  return {
    intersectionObserver,
    visibleResultsRef,
  };
}
